import React from 'react';
import { SelfRegistrationSessionDTO } from '@/shared/api/model';
// import StampComponent from '@/modules/loans/components/StampComponent';
// import { DeductionFormConfig, DEFAULT_TEXT_CONTENT } from '@/modules/loans/components/DeductionFormConfig';

// Stub types for missing loans module
interface DeductionFormConfig {
    header?: any;
    branding?: any;
    sections?: any;
    stamp?: any;
    textContent?: any;
    company?: any;
}

const DEFAULT_TEXT_CONTENT: any = {};

// Stub StampComponent
const StampComponent: any = () => null;

interface SelfRegistrationDeductionFormProps {
    session: SelfRegistrationSessionDTO;
    company: any;
    deductionFormConfig?: DeductionFormConfig;
}

const SelfRegistrationDeductionForm: React.FC<SelfRegistrationDeductionFormProps> = ({ session, company, deductionFormConfig }) => {
    const currentDate = new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });

    const text = { ...DEFAULT_TEXT_CONTENT, ...deductionFormConfig?.textContent };

    const employerName = session.employerName || 'EMPLOYER NAME';
    const employerAddressRaw = ((session as any).employerAddress as string | undefined) || '';
    const employerAddressLines = employerAddressRaw
        ? employerAddressRaw
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter(Boolean)
        : [];

    const officerInitials = ((session as any).officerInitials as string | undefined) || '';
    const officerName = ((session as any).officerName as string | undefined) || session.officerApprovedBy || '';
    const officerPhone = ((session as any).officerPhone as string | undefined) || '';

    const formattedAmount = session.monthlyDeductionAmount
        ? Number(session.monthlyDeductionAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '0.00';

    const configCompany = deductionFormConfig?.company || {};
    const companyLogoUrl: string = deductionFormConfig?.branding?.logoUrl || configCompany?.logoUrl || company?.logoUrl || company?.logo || '';
    const watermarkUrl: string = deductionFormConfig?.branding?.watermarkUrl || companyLogoUrl;
    const showWatermark = Boolean(deductionFormConfig?.branding?.showWatermark && watermarkUrl);
    const companyName: string = configCompany?.name || company?.name || company?.brFname || '';
    const companyAddress: string = configCompany?.address || company?.address || '';
    const companyPhone: string = configCompany?.phone || company?.phone || '';
    const companyEmail: string = configCompany?.email || company?.email || '';
    const companyWebsite: string = configCompany?.website || company?.website || '';
    const companyPoBox: string = configCompany?.poBox || company?.poBox || '';
    const companyLocation: string = configCompany?.location || company?.location || '';

    return (
        <div style={{
            fontFamily: 'Arial, sans-serif',
            padding: '20px',
            maxWidth: '800px',
            margin: '0 auto',
            backgroundColor: 'white',
            color: 'black',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Watermark */}
            {showWatermark && (
                <div
                    aria-hidden
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${watermarkUrl})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundSize: '70%',
                        opacity: 0.06,
                        pointerEvents: 'none',
                        zIndex: 0
                    }}
                />
            )}

            <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Header with logos and company info */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    borderBottom: '2px solid #000',
                    paddingBottom: '10px'
                }}>
                    <div style={{ width: '80px', height: '80px' }}>
                        {companyLogoUrl && (
                            <img
                                src={companyLogoUrl}
                                alt="Company Logo"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        )}
                    </div>
                    <div style={{ textAlign: 'center', flex: 1, padding: '0 20px' }}>
                        <h2 style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: 'bold' }}>
                            {companyName}
                        </h2>
                        {(companyAddress || companyPhone) && (
                            <p style={{ margin: '0', fontSize: '11px' }}>
                                {companyAddress}{companyPhone ? ` Tel: ${companyPhone}` : ''}
                            </p>
                        )}
                        {(companyEmail || companyWebsite) && (
                            <p style={{ margin: '0', fontSize: '11px' }}>
                                {companyEmail && <>Email: <a href={`mailto:${companyEmail}`}>{companyEmail}</a></>}
                                {companyEmail && companyWebsite && ', '}
                                {companyWebsite && <>Website: <a href={companyWebsite}>{companyWebsite}</a></>}
                            </p>
                        )}
                    </div>
                    <div style={{ width: '80px', height: '80px' }}>
                        {companyLogoUrl && (
                            <img
                                src={companyLogoUrl}
                                alt="Company Logo"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        )}
                    </div>
                </div>

                {/* Reference and Date */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '12px' }}>
                    <div>
                        <strong>Kumb Na {text.referencePrefix}/{session.sessionToken?.substring(0, 8).toUpperCase()}/{new Date().getFullYear()}</strong>
                    </div>
                    <div>
                        <strong>{currentDate}</strong>
                    </div>
                </div>

                {/* Employer Address */}
                <div style={{ marginBottom: '20px', fontSize: '12px' }}>
                    {employerAddressLines.length > 0 ? (
                        employerAddressLines.map((line, idx) => (
                            <p key={idx} style={{ margin: '2px 0' }}><strong>{line}</strong></p>
                        ))
                    ) : (
                        <p style={{ margin: '2px 0' }}><strong>EMPLOYER ADDRESS</strong></p>
                    )}
                </div>

                {/* Subject */}
                <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                    <h3 style={{
                        margin: '10px 0',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        textDecoration: 'underline'
                    }}>
                        YAH: {text.subjectLine} {session?.fullName?.toUpperCase()}  WA VOTE {session.voteNumber || ''}
                    </h3>
                </div>

                {/* Body */}
                <div style={{ marginBottom: '20px', fontSize: '12px', lineHeight: '1.6' }}>
                    <p>
                        Tunawasilisha Jina la mwanachama wa {companyName} {text.bodyText} kwa
                        mwezi <strong>{new Date().toLocaleDateString('sw-TZ', { month: 'long', year: 'numeric' })}</strong> na kuendelea.
                    </p>
                    <p>
                        Pamoja na barua hii jina la mwanachama huyo yameambalanishwa kwa mchanganuo Zaidi
                    </p>
                </div>

                {/* Savings Table */}
                <div style={{ marginBottom: '30px' }}>
                    <h4 style={{
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: 'bold',
                        marginBottom: '10px'
                    }}>
                        SAVING ONS
                    </h4>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '11px'
                    }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>S/N</th>
                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>NAME</th>
                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>CHECK NO</th>
                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>VOTE</th>
                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>CC</th>
                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>CODE</th>
                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>ONS</th>
                                <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>BALANCE</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>1.</td>
                                <td style={{ border: '1px solid #000', padding: '8px' }}>{session.fullName || ''}</td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{session.employeeId || ''}</td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{session.voteNumber || ''}</td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>3</td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>FC7793</td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>{formattedAmount}</td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>-</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Signature Section */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '40px',
                    fontSize: '11px'
                }}>
                    {/* Left side */}
                    <div style={{ width: '45%' }}>
                        <p style={{ marginBottom: '5px' }}><strong>Tunashukuru kwa ushirikiano wako.</strong></p>
                        <div style={{ marginTop: '60px' }}>
                            <div style={{ marginBottom: '40px' }}>
                                <p style={{ margin: '0' }}><strong>Ushirika Daima</strong></p>
                            </div>
                            <div style={{ marginBottom: '40px' }}>
                                <p style={{ margin: '0' }}><em>{officerInitials || ''}</em></p>
                                <div style={{ borderBottom: '1px solid #000', width: '200px', marginBottom: '5px' }}></div>
                            </div>
                            <div>
                                <p style={{ margin: '0' }}><em>{officerName}</em></p>
                                <div style={{ borderBottom: '1px solid #000', width: '200px', marginBottom: '5px' }}></div>
                            </div>
                            {officerPhone && <p style={{ marginTop: '20px' }}><strong>{officerPhone}</strong></p>}
                        </div>
                        <StampComponent
                            config={deductionFormConfig?.stamp}
                            size={140}
                            rotate={-12}
                            style={{ marginTop: '20px', opacity: 0.85 }}
                        />
                    </div>

                    {/* Right side - Member consent */}
                    <div style={{ width: '45%' }}>
                        <p style={{ marginBottom: '10px' }}><strong>Imepokelewa na kufanyiwa kazi na:</strong></p>
                        <div style={{ marginBottom: '15px' }}>
                            <p style={{ margin: '5px 0' }}>Jina: <span style={{ borderBottom: '1px dotted #000', display: 'inline-block', minWidth: '200px' }}></span></p>
                            <p style={{ margin: '5px 0' }}>Cheo: <span style={{ borderBottom: '1px dotted #000', display: 'inline-block', minWidth: '200px' }}></span></p>
                            <p style={{ margin: '5px 0' }}>Saini: <span style={{ borderBottom: '1px dotted #000', display: 'inline-block', minWidth: '200px' }}></span></p>
                            <p style={{ margin: '5px 0' }}>Tarehe: <span style={{ borderBottom: '1px dotted #000', display: 'inline-block', minWidth: '200px' }}></span></p>
                            <p style={{ margin: '5px 0' }}>Mhuri: <span style={{ borderBottom: '1px dotted #000', display: 'inline-block', minWidth: '200px' }}></span></p>
                        </div>

                        {/* Member Consent */}
                        <div style={{
                            border: '2px solid #000',
                            padding: '15px',
                            marginTop: '30px',
                            backgroundColor: '#f9f9f9'
                        }}>
                            <h4 style={{
                                margin: '0 0 10px 0',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                textDecoration: 'underline'
                            }}>
                                {text.consentTitle}
                            </h4>
                            <p style={{ fontSize: '11px', lineHeight: '1.5', marginBottom: '10px' }}>
                                Mimi, <strong>{session.fullName || '___________________'}</strong>, ninakubali na kuthibitisha kwamba:
                            </p>
                            <div style={{ fontSize: '10px', lineHeight: '1.6', marginBottom: '15px', paddingLeft: '20px' }}>
                                {Array.isArray(text.consentItems) && text.consentItems.length > 0 ? (
                                    text.consentItems.map((item: string, idx: number) => (
                                        <div key={idx} style={{ marginBottom: '4px', textIndent: '-20px', paddingLeft: '20px' }}>
                                            {idx + 1}. {(item || '').replace('{amount}', `TZS ${formattedAmount}`).replace('{companyName}', companyName)}
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ textIndent: '-20px', paddingLeft: '20px' }}>
                                        1. Ninakubali makato ya TZS {formattedAmount} yanatolewa kila mwezi kutoka kwenye mshahara wangu.
                                    </div>
                                )}
                            </div>
                            <div style={{ marginTop: '30px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <span style={{ fontSize: '11px' }}><strong>Sahihi ya Mwanachama:</strong></span>
                                    <div style={{ borderBottom: '1px solid #000', width: '150px' }}></div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '11px' }}><strong>Tarehe:</strong></span>
                                    <div style={{ borderBottom: '1px solid #000', width: '150px' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer note */}
                {text.footerNote && (
                    <div style={{
                        marginTop: '40px',
                        fontSize: '10px',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        color: '#666'
                    }}>
                        <p>{text.footerNote}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SelfRegistrationDeductionForm;
